import { gql } from '@apollo/client';
import { useClipboard, useDisclosure } from '@dwarvesf/react-hooks';
import { Box, BoxProps, Typography } from '@mui/material';
import { IconButtonWithTooltip } from 'components/common/IconButton/IconButtonWithTooltip';
import { toast } from 'components/common/Toast';
import { Tooltip } from 'components/common/Tooltip';
import { IconCustomExpire } from 'components/icons/components/custom/IconCustomExpire';
import { IconLinearExport2 } from 'components/icons/components/linear/IconLinearExport2';
import { IconOutlineDislike } from 'components/icons/components/outline/IconOutlineDislike';
import { IconOutlineLink2 } from 'components/icons/components/outline/IconOutlineLink2';
import {
  SocialListeningContentCalendarSaveButton,
  SocialListeningPostSaveButton,
  useInstagramStoryPostState,
  useSocialListeningPostLinkGenerator,
} from 'features/socialMediaListening';
import { useRenderPostReportedSnackbar } from 'features/socialMediaListening/hooks/useRenderPostReportedSnackbar';
import {
  platformIconMap,
  SocialPostBrandEngagementStatus,
} from 'features/socialPost';
import {
  BrandFragmentSocialMediaListeningPostAnalysisViewFragment,
  SocialPostFragmentSocialMediaListeningPostAnalysisViewFragment,
  SocialPostType,
  useGetCreatorProfileStatsByCreatorSlPostDetailQuery,
} from 'graphql/generated';
import moment from 'moment';
import { useMemo, useRef, useState } from 'react';
import { theme } from 'styles/theme';
import { SocialMediaListeningContentTabs } from '../postSummary/sections/postVideoDialog/sections/SocialMediaListeningContentTabs';
import { SocialListeningReportPostView } from '../reportPost/SocialListeningReportPostView';
import { SocialMediaListeningPostDetailCarousel } from './SocialMediaListeningPostDetailCarousel';
import { SocialMediaListeningPostDetailImage } from './SocialMediaListeningPostDetailImage';
import { SocialMediaListeningPostDetailVideo } from './SocialMediaListeningPostDetailVideo';
import { TitleWithInfoTooltip } from './TitleWithInfoTooltip';

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
gql`
  fragment SocialPostFragmentSocialMediaListeningPostAnalysisView on SocialPostModel {
    id
    expiresAt
    totalContentCalendarSaves
    isSavedToContentCalendar
    savedPost {
      id
      isFavorite
      isFavoriteTrend
    }
    type
    carouselMedias {
      id
      type
      url
    }
    totalPostSaves
    sentimentTexts {
      id
      sentiment
      keyword
      category
      subCategory
      brandId
    }
    ...SocialPostFragmentSocialMediaListeningPostSummary
    ...SocialPostFragmentBrandSocialPostEngagementStatus
    socialPostEngagementSuggestionsWithTopic(
      topicIds: $topicIds
      brandId: $brandId
    ) {
      id
      __typename
      sentiment
      score
      suggestedComment
      oneLineSummary
      reason
      topic {
        id
        name
        ...TopicFragmentTopicLabel
      }
    }
    socialPostEngagementSuggestions(brandId: $brandId) {
      id
      __typename
      sentiment
      score
      suggestedComment
      oneLineSummary
      reason
      topic {
        id
        name
        ...TopicFragmentTopicLabel
      }
    }
    ...SocialPostFragmentWhatYouCanSay
  }
`;

gql`
  fragment BrandFragmentSocialMediaListeningPostAnalysisView on BrandModel {
    id
    name
  }
`;

export type SocialMediaListeningPostAnalysisViewProps = {
  socialPost: SocialPostFragmentSocialMediaListeningPostAnalysisViewFragment;
  brand:
    | BrandFragmentSocialMediaListeningPostAnalysisViewFragment
    | null
    | undefined;
  topicIds?: string[];
  componentProps?: {
    container?: BoxProps;
  };
};

export const SocialMediaListeningPostAnalysisView = ({
  socialPost,
  brand,
  topicIds,
  componentProps,
}: SocialMediaListeningPostAnalysisViewProps) => {
  const detailsVideoRef = useRef<{
    seekVideoTime: (time: number) => void;
    currentVideoTime: number;
  }>(null);
  const [renderedHeight, setRenderedHeight] = useState(0);
  const { generateLink } = useSocialListeningPostLinkGenerator();
  const { onCopy: onCopyLink } = useClipboard(
    socialPost ? generateLink(socialPost) : '',
  );
  const { isPostAStory, hasExpired, isExpiringSoon, timeAgo } =
    useInstagramStoryPostState({ socialPost });

  const { renderToast, showToast } = useRenderPostReportedSnackbar();

  const socialPostEngagementSuggestions =
    (topicIds as string[])?.length &&
    socialPost.socialPostEngagementSuggestionsWithTopic.length
      ? socialPost.socialPostEngagementSuggestionsWithTopic
      : socialPost.socialPostEngagementSuggestions;

  const socialPostEngagementSuggestion = socialPostEngagementSuggestions?.[0];

  const allTime = useMemo(
    () => ({
      startDate: moment().subtract(2, 'years').toDate(),
      endDate: moment().endOf('day').toDate(),
    }),
    [],
  );

  const { data: creatorProfileStats } =
    useGetCreatorProfileStatsByCreatorSlPostDetailQuery({
      variables: {
        input: {
          creatorId: socialPost?.creator?.id ?? '',
          brandId: brand?.id ?? '',
          dateRange: allTime,
        },
      },
      skip: !brand?.id || !socialPost?.creator?.id,
    });

  /**
   * Render the icons on top of the post video/image/carousel/etc.
   * See: https://www.figma.com/design/yx4TIYR2lZV8q9a1HbeoA5/%5BWeb%5D-Social-listening?node-id=4500-356397&node-type=frame&t=0WWrkD28ospXDOeN-0
   * I put it here because there's not a good place to put it for now.
   */
  const postTopIconsRender = useMemo(() => {
    if (!socialPost) {
      return null;
    }

    const Icon = platformIconMap[socialPost.platform];

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          p: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: theme.colors?.primary.parchment,
          width: '100%',
        }}
      >
        {Icon && <Icon size={20} />}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            button: {
              borderRadius: 50,
              bgcolor: '#2306031A',
              color: 'inherit !important',
              backdropFilter: 'blur(40px)',
            },
          }}
        >
          <IconButtonWithTooltip
            tooltip="Copy link"
            size="small"
            sx={{
              p: 1,
            }}
            onClick={() => {
              onCopyLink();
              toast({
                type: 'success',
                message: 'Link copied to clipboard',
              });
            }}
          >
            <IconOutlineLink2 size={16} />
          </IconButtonWithTooltip>
          {isPostAStory && (
            <Tooltip
              title={moment(socialPost.expiresAt).format('MMM DD, YYYY h:mm A')}
            >
              <Box
                component="button"
                sx={{
                  p: 1,
                  pr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  ...(isExpiringSoon && {
                    bgcolor: `${theme.colors?.utility['pink-3']} !important`,
                  }),
                  ...(hasExpired && {
                    bgcolor: `${theme.colors?.utility[600]} !important`,
                  }),
                }}
              >
                <IconCustomExpire size={16} />
                <Typography variant="headline-xs">{timeAgo}</Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  }, [socialPost, onCopyLink]);

  const {
    isOpen: isReportPostDialogOpen,
    onClose: closeReportPostDialog,
    onOpen: openReportPostDialog,
  } = useDisclosure();

  return (
    <>
      <Box
        {...componentProps?.container}
        sx={{
          backgroundColor: 'rgba(246, 229, 216, 0.25)',
          borderRadius: 4,
          p: 9,
          display: 'flex',
          gap: 4,
          flexDirection: 'column',
          ...componentProps?.container?.sx,
        }}
      >
        <Box width="100%" display="flex" justifyContent="space-between">
          {socialPost.type === SocialPostType.Video ? (
            <TitleWithInfoTooltip
              title="Video Analysis"
              info="Video analysis provides insights into the content, creator, and key highlights relevant to your topic."
            />
          ) : (
            <TitleWithInfoTooltip
              title="Image Analysis"
              info="Image analysis provides insights into the content, creator, and key highlights relevant to your topic."
            />
          )}
          <Box display="flex" gap={4} alignItems="center">
            <Box
              display="flex"
              color={theme.colors?.utility['pink-3']}
              gap={2}
              alignItems="center"
              fontWeight={600}
              sx={{ cursor: 'pointer' }}
              onClick={openReportPostDialog}
            >
              <IconOutlineDislike size={16} />
              <Typography variant="subhead-lg">Report Post</Typography>
            </Box>
            <SocialPostBrandEngagementStatus
              brandId={brand?.id ?? ''}
              socialPost={socialPost}
              showIcon
              componentProps={{
                button: {
                  sx: {
                    p: theme.spacing(1, 2),
                  },
                },
              }}
            />
          </Box>
        </Box>
        <Box display="flex" gap={4}>
          <Box flex={2} sx={{ overflow: 'hidden' }}>
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              position="relative"
            >
              {socialPost.type === SocialPostType.Video && (
                <SocialMediaListeningPostDetailVideo
                  post={socialPost}
                  onHeightSet={(height) => {
                    setRenderedHeight(height);
                  }}
                  ref={detailsVideoRef}
                />
              )}
              {socialPost.type === SocialPostType.Carousel && (
                <SocialMediaListeningPostDetailCarousel
                  post={socialPost}
                  onHeightSet={(height) => {
                    setRenderedHeight(height);
                  }}
                />
              )}
              {socialPost.type === SocialPostType.Photo && (
                <SocialMediaListeningPostDetailImage
                  post={socialPost}
                  onHeightSet={(height) => {
                    setRenderedHeight(height);
                  }}
                />
              )}
              {socialPostEngagementSuggestion && (
                <Box display="flex" gap={4} alignItems="center">
                  <SocialListeningPostSaveButton
                    socialListeningPost={socialPost}
                    socialPostEngagementSuggestionId={
                      socialPostEngagementSuggestion.id
                    }
                  />
                  <SocialListeningContentCalendarSaveButton
                    socialPost={socialPost}
                    socialPostEngagementSuggestionId={
                      socialPostEngagementSuggestion.id
                    }
                  />
                </Box>
              )}
              {postTopIconsRender}
            </Box>
          </Box>
          <Box flex={3} sx={{ overflow: 'auto' }}>
            <Box display="flex" flexDirection="column" gap={4}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="headline-lg">
                  {socialPostEngagementSuggestion?.oneLineSummary}
                </Typography>
                <Box
                  width="fit-content"
                  component="a"
                  href={generateLink(socialPost)}
                  target="_blank"
                >
                  <Typography
                    variant="body-xl"
                    fontWeight={500}
                    color={theme.colors?.utility[800]}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    Visit <IconLinearExport2 size={16} />
                  </Typography>
                </Box>
              </Box>
              <SocialMediaListeningContentTabs
                brandId={brand?.id ?? ''}
                post={socialPost}
                creatorProfileStats={
                  creatorProfileStats?.getCreatorProfileByCreatorId ?? undefined
                }
                currentVideoTimeInSeconds={
                  detailsVideoRef.current?.currentVideoTime || 0
                }
                componentProps={{
                  videoHighlights: {
                    onHighlightClicked: (time) => {
                      detailsVideoRef.current?.seekVideoTime?.(time);
                    },
                  },
                  postSummary: {
                    containerSx: {
                      backgroundColor: 'rgba(240, 183, 134, 0.15)',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <SocialListeningReportPostView
        isOpen={isReportPostDialogOpen}
        onClose={() => {
          closeReportPostDialog();
        }}
        handleOnSubmit={() => {
          showToast();
        }}
        postId={socialPost?.id}
        brandId={brand?.id ?? ''}
      />
      {renderToast()}
    </>
  );
};
